/**
 * Verification-check vocabulary + DTOs (KYCID-FSD-00013 §8).
 *
 * A 1:1 mirror of the backend `com.merlinai.vcip.check` enums + the read DTOs the
 * droom consumes (`GET /sessions/{id}/checks`). The FE cannot import the Java
 * enums, so this is the hand-kept shadow — kept byte-aligned with the wire.
 *
 * The customer flow is PLAN-DERIVED, never a module constant: the frozen
 * per-session plan (server-authoritative) decides which PRE_CALL checks appear
 * and in what order. This module is the shared type spine both the flow machine
 * (`flow/checkPlan.ts`) and the store reason over.
 */

/** The registered checks (mirror of `CheckKey`). Unknown wire keys degrade to a
 * MissingCheckStep — the union is the KNOWN set, not a closed-world assertion. */
export type CheckKey =
  | 'DIGILOCKER_AADHAAR'
  // KYCID·00001-00006-00002: the 12th CheckKey, previously absent from this union while the
  // backend has always registered it. Under the provenance realignment (PH-1) it is a
  // first-class PRE_CALL authoritative-source rail, so an absent key would have degraded a
  // planned check to a MissingCheckStep — the honest fallback, but the wrong answer here.
  | 'DIGILOCKER_PAN'
  | 'PAN_SOURCE'
  | 'DOC_CAPTURE'
  | 'CONSENT'
  | 'FACE_MATCH'
  | 'LIVENESS'
  | 'GEO_IP'
  | 'AGENT_QUESTIONS'
  | 'AML_SCREENING'
  // INTAKE-09 §3.1 — the two configurable evidence-input checks (owner directive,
  // 2026-07-24). SELFIE: guided face self-capture, canonical PRE_CALL (grade E3).
  // SELFIE_WITH_DOC: customer + document co-framed in ONE frame, canonical IN_CALL
  // (E2) with a legal PRE_CALL variant — the pinned plan's phase decides where it
  // renders; the droom only ever renders the PRE_CALL rows.
  | 'SELFIE'
  | 'SELFIE_WITH_DOC'
  // EBIX-1 (CD6 §1; pack §B.2) — the three per-document keys, added together with the be vocabulary
  // (append-once). All three are canonical IN_CALL (officer-directed document ceremonies; DEC-057
  // uploads/mid-migration make PRE_CALL legal), so NONE joins CUSTOMER_FACING_PRECALL_CHECKS and none
  // has a bespoke pre-call step — a tenant pinning the PRE_CALL variant renders the honest
  // MissingCheckStep (continue-with-agent), never a white screen. ADDRESS_PROOF is excluded from
  // name-match by construction (§B.2).
  | 'PASSPORT'
  | 'ADDRESS_PROOF'
  | 'VOTER_ID';

/** Where a check runs (mirror of `CheckPhase`). */
export type CheckPhase = 'PRE_CALL' | 'IN_CALL' | 'POST_CALL';

/** The (tenant×flow) requirement after the regpack floor clamp (mirror of `CheckRequirement`). */
export type CheckRequirement = 'OFF' | 'OPTIONAL' | 'REQUIRED';

/** Per-check status (mirror of `CheckStatus`). */
export type CheckStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'PASSED'
  | 'FAILED'
  | 'EXPIRED'
  | 'SKIPPED'
  | 'FALLBACK_VERIFIED'
  | 'NOT_APPLICABLE';

/** Derived session rollup (mirror of `OverallVerificationStatus`). */
export type OverallVerificationStatus =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'PENDING_REQUIRED'
  | 'PENDING_POST_CALL'
  | 'READY_FOR_DECISION'
  | 'DECISION_APPROVED'
  | 'DECISION_REJECTED'
  | 'COMPLETE';

/** One pinned plan row — a check, its phase, its clamped requirement, its order. */
export interface PlanCheckDTO {
  readonly checkKey: CheckKey;
  readonly phase: CheckPhase;
  readonly requirement: CheckRequirement;
  readonly order: number;
}

/** One upserted rollup-input row. */
export interface CheckStatusDTO {
  readonly checkKey: CheckKey;
  readonly status: CheckStatus;
  readonly verdict?: string | null;
  readonly evidenceRef?: string | null;
  readonly updatedAt?: string | null;
}

/** The `/sessions/{id}/checks` read: plan ⋈ status + the derived overall status. */
export interface SessionChecksDTO {
  readonly sessionId: string;
  readonly overallStatus: OverallVerificationStatus;
  readonly plan: readonly PlanCheckDTO[];
  readonly statuses: readonly CheckStatusDTO[];
  /** The write-once pinned flow key (present when the BE surfaces it). */
  readonly flowKey?: string | null;
  /**
   * Additive (this release): the tenant's branding block ({name, brandColor,
   * …, support}) served on the bootstrap read. UNTRUSTED wire value — parse
   * via `theme/servedTenant.parseServedBranding`; absent on an older be.
   */
  readonly branding?: unknown;
}

/** The `/sessions/{id}/overall-status` derived poll. */
export interface OverallStatusDTO {
  readonly overallStatus: OverallVerificationStatus;
  readonly requiredTotal: number;
  readonly requiredSatisfied: number;
}

/** One flow in the tenant flow registry. */
export interface FlowSummaryDTO {
  readonly flowKey: string;
  readonly label: string;
  readonly active: boolean;
}

/** One (flow,check) config cell — the admin matrix row. */
export interface CheckConfigDTO {
  readonly checkKey: CheckKey;
  readonly phase: CheckPhase;
  readonly requirement: CheckRequirement;
  /** Where the effective requirement resolved from (FLOW | TENANT | SEED | REGPACK). */
  readonly inheritedFrom: string;
  /** False = the regime forbids this rail → rendered LOCKED, never hidden. */
  readonly regpackPermitted: boolean;
}

/** A flow step id for a keyed check — `check:${CheckKey}` (KYCID-FSD-00013 §8). */
export type CheckStepId = `check:${CheckKey}`;

const CHECK_STEP_PREFIX = 'check:';

/** Build the `check:${key}` step id for a check. */
export function checkStepId(key: CheckKey): CheckStepId {
  return `${CHECK_STEP_PREFIX}${key}`;
}

/** Type guard: is this step id a keyed check step? */
export function isCheckStepId(step: string): step is CheckStepId {
  return step.startsWith(CHECK_STEP_PREFIX);
}

/** Extract the {@link CheckKey} from a `check:${key}` step id. */
export function checkKeyOfStep(step: CheckStepId): CheckKey {
  return step.slice(CHECK_STEP_PREFIX.length) as CheckKey;
}
